import {
  InfoCircledIcon,
  KeyboardIcon,
  LockClosedIcon,
  PaperPlaneIcon,
} from "@radix-ui/react-icons";
import {
  Auth0AccountRole,
  MessageDirection,
  MessageResponse,
  MessagesService,
  MessageType,
  OrgMessageRecipient,
} from "client/openapi";
import { PersonKind } from "client/openapi/models/PersonKind";
import { Button, ButtonColor, ButtonFill, ButtonSize } from "components/Button";
import { useContext, useEffect, useRef, useState } from "react";
import Notifications from "util/notifications";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";
import { usePersonalReplyAddress } from "util/personalAddress";
import { newestInbound, rowChannel, threadPeople } from "./conversations";
import { privateTo } from "./privacy";
import EmailToLine, {
  answeredEmailPeople,
  EmailRecipient,
  toMessageRecipients,
} from "./EmailToLine";

/** Unsent replies by row, kept while Tadpole is open. */
const drafts = new Map<string, string>();

/** Who a text reply goes to: the people of the row's newest message. */
function replyRecipients(messages: MessageResponse[]) {
  const latest = messages[messages.length - 1];
  const sent = latest.direction === MessageDirection.OUTBOUND;
  const students =
    (sent ? latest.student_recipients : latest.matched_students) ?? [];
  const tutors = (sent ? latest.tutor_recipients : latest.matched_tutors) ?? [];
  const parents =
    (sent ? latest.parent_recipients : latest.matched_parents) ?? [];
  return {
    recipient_types: [
      ...(students.length ? [OrgMessageRecipient.STUDENTS] : []),
      ...(tutors.length ? [OrgMessageRecipient.TUTORS] : []),
      ...(parents.length ? [OrgMessageRecipient.PARENTS] : []),
    ],
    student_recipients: students.map((s) => s.id),
    tutor_recipients: tutors.map((t) => t.id),
    parent_recipients: parents.map((p) => p.id),
  };
}

/**
 * The reply box under a conversation in the Communication view, by the
 * row's channel. A text goes to the row's people. An email
 * answers one email of the thread (the newest, or the one whose Reply was
 * clicked) and goes to the people on its To line. Ctrl or Cmd plus Enter
 * sends. While it is empty, a suggested reply shows in it in light gray, and
 * Tab puts it in; typing hides it, and clearing the box brings it back.
 */
export default function ReplyBox({
  row,
  messages,
  orgId,
  answered,
  people,
  newer,
  onAnswer,
  onEdit,
  focusKey,
  suggestion,
  onSent,
}: {
  row: string;
  /** The row's messages, oldest first. */
  messages: MessageResponse[];
  orgId: number;
  /** For an email row: the email being answered. */
  answered?: MessageResponse;
  /** For an email row: everyone who can be added to the To line. */
  people?: EmailRecipient[];
  /** A newer email that came in after this reply was started. */
  newer?: MessageResponse;
  /** Answer another email (the newer one). */
  onAnswer?: (message: MessageResponse) => void;
  /** The admin started on the reply: keep answering this email. */
  onEdit?: () => void;
  /** Changes each time the cursor should come here (Reply clicked on an email). */
  focusKey?: number;
  /** Claude's draft reply to the message being answered. */
  suggestion?: string;
  onSent: (message: MessageResponse) => void;
}) {
  const [content, setContent] = useState(drafts.get(row) ?? "");
  // A draft left from before counts as started: keep answering this email.
  const draftAtStart = useRef(!!content);
  useEffect(() => {
    if (!draftAtStart.current) return;
    draftAtStart.current = false;
    onEdit?.();
  }, [onEdit]);
  const [subject, setSubject] = useState("");
  const [sending, setSending] = useState(false);
  const textarea = useRef<HTMLTextAreaElement>(null);

  const channel = rowChannel(messages);
  const email = channel === "email" && !!answered;
  // An email reply goes to the answered email's people, or whoever is added.
  const [to, setTo] = useState<EmailRecipient[]>(
    answered ? answeredEmailPeople(answered) : []
  );
  // A different email answered: its people. (A reload keeps edits to To.)
  const toFor = useRef(answered?.id);
  useEffect(() => {
    if (!answered || toFor.current === answered.id) return;
    toFor.current = answered.id;
    setTo(answeredEmailPeople(answered));
  }, [answered]);

  const repliedTo = email ? answered : newestInbound(messages);
  // A reply to mail kept to some admins is kept to them too, unless it goes
  // to an office address (then every admin has it).
  const { account, currently_selected_role } = useContext(
    OrgRolesAndAccountContext
  );
  const keptTo = privateTo(repliedTo, account?.reference_id);
  // An email reply to the people on the email it answers goes as the admin's
  // own personal address (from it, or with Reply-To it), so the answer comes
  // back to their mailbox, and only to them, even on a thread every admin sees.
  const answersTo = usePersonalReplyAddress(
    orgId,
    email &&
      (currently_selected_role === Auth0AccountRole.ORG_ADMIN ||
        currently_selected_role === Auth0AccountRole.TADPOLE_ADMIN)
  );
  const onTheEmail =
    !!answered &&
    to.length > 0 &&
    to.every((person) =>
      answeredEmailPeople(answered).some(
        (p) => p.role === person.role && p.id === person.id
      )
    );
  const privacy = !keptTo
    ? answersTo && onTheEmail
      ? `${
          to.some((person) => person.role !== PersonKind.TUTOR)
            ? "The family's answer"
            : "The answer"
        } to this reply goes to ${answersTo}, so it comes back only to you.`
      : undefined
    : repliedTo?.reply_shared
    ? "Every admin will see your reply, because it goes to an office address."
    : `Only ${keptTo} will see your reply, as with the email it answers.`;
  // A new email thread (nothing received yet) needs a subject.
  const needsSubject = channel === "email" && !repliedTo;
  const canSend =
    !!content.trim() &&
    !sending &&
    (!needsSubject || !!subject.trim()) &&
    (!email || to.length > 0);

  useEffect(() => {
    if (focusKey) textarea.current?.focus();
  }, [focusKey]);

  // The suggestion shows only while the box is empty.
  const suggested = !content && suggestion ? suggestion : undefined;

  // Grow with the text, or with the suggestion shown in its place.
  useEffect(() => {
    const el = textarea.current;
    if (!el) return;
    el.style.height = "auto";
    if (suggested) el.value = suggested;
    el.style.height = `${el.scrollHeight}px`;
    if (suggested) el.value = "";
  }, [content, suggested]);

  function update(text: string) {
    if (text) onEdit?.();
    setContent(text);
    if (text) drafts.set(row, text);
    else drafts.delete(row);
  }

  async function send() {
    if (!canSend) return;
    setSending(true);
    try {
      const sent = await MessagesService.createMessageToRecipients({
        requestBody: {
          m_type: MessageType.DIRECT,
          subject: needsSubject ? subject : "",
          content,
          org_id: orgId,
          email: channel === "email",
          sms: channel === "sms",
          direction: MessageDirection.OUTBOUND,
          // A reply an admin writes goes from their own confirmed address.
          from_personal: true,
          ...(email ? toMessageRecipients(to) : replyRecipients(messages)),
          ...(repliedTo && { message_being_replied_to_id: repliedTo.id }),
        },
      });
      update("");
      setSubject("");
      // Someone added to the To line has an office address: every admin sees it.
      if (keptTo && !sent.private_to?.length) {
        Notifications.info(
          "Every admin can see your reply, because it went to an office address."
        );
      }
      onSent(sent);
    } catch (e: any) {
      console.error(`Error sending message: ${e.message}`);
      Notifications.error("Unable to send message.");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="flex flex-col gap-2">
      {email && newer && (
        <p className="text-xs text-gray-600">
          New email in this thread ·{" "}
          <button
            type="button"
            className="font-semibold text-[color:var(--green)] hover:underline"
            onClick={() => onAnswer?.(newer)}
          >
            Reply to it
          </button>
        </p>
      )}
      {email && (
        <EmailToLine
          value={to}
          onChange={(value) => {
            onEdit?.();
            setTo(value);
          }}
          people={people ?? threadPeople(messages)}
          suggested={threadPeople(messages)}
        />
      )}
      {privacy && (
        <p className="flex items-center gap-1 text-xs font-medium text-gray-600">
          {!keptTo || repliedTo?.reply_shared ? (
            <InfoCircledIcon aria-hidden />
          ) : (
            <LockClosedIcon aria-hidden />
          )}
          {privacy}
        </p>
      )}
      {needsSubject && (
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Enter subject..."
          className="w-1/2 p-2 border border-gray-300 rounded-xl"
        />
      )}
      <div className="flex items-start gap-2">
        <div className="relative flex-1">
          <textarea
            ref={textarea}
            value={content}
            onChange={(e) => update(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                send();
              }
              if (e.key === "Tab" && !e.shiftKey && suggested) {
                e.preventDefault();
                update(suggested);
              }
            }}
            placeholder={
              suggested ?? `Type your ${channel === "sms" ? "SMS" : "email"}...`
            }
            aria-describedby={suggested ? `${row}-suggestion` : undefined}
            rows={3}
            className={`block w-full p-2 border border-gray-300 rounded-xl resize-none overflow-hidden leading-6 ${
              suggested ? "pb-8 placeholder:text-gray-300" : ""
            }`}
          />
          {suggested && (
            <span
              id={`${row}-suggestion`}
              className="pointer-events-none absolute bottom-2 right-3 flex items-center gap-1 text-xs font-semibold text-[color:var(--green)]"
            >
              <KeyboardIcon aria-hidden />
              Press Tab to accept
            </span>
          )}
        </div>
        <Button
          size={ButtonSize.SMALL}
          color={canSend ? ButtonColor.DARK_BLUE : ButtonColor.DARK_GRAY}
          fill={ButtonFill.HOLLOW}
          onClick={send}
          disabled={!canSend}
          title="Send (Ctrl or Cmd + Enter)"
        >
          {sending ? "Sending..." : "Send"} <PaperPlaneIcon />
        </Button>
      </div>
    </div>
  );
}
